// Accessories drawer functionality
(function() {
  // Gate noisy debug logging behind either:
  // - `window.DYC_DEBUG = true` (e.g., in theme editor preview)
  // - `?dyc_debug=1` in the URL
  const DYC_DEBUG =
    (typeof window !== 'undefined' && (window.DYC_DEBUG === true || window.DYC_DEBUG === 'true')) ||
    (typeof window !== 'undefined' && /(?:\\?|&)dyc_debug=1\\b/.test(window.location.search));

  const console = DYC_DEBUG
    ? window.console
    : {
        log() {},
        info() {},
        debug() {},
        warn() {},
        // Keep errors visible even when debug is off
        error:
          typeof window !== 'undefined' && window.console && window.console.error
            ? window.console.error.bind(window.console)
            : function () {},
      };

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccessoriesDrawer);
  } else {
    initAccessoriesDrawer();
  }
  
  function initAccessoriesDrawer() {
    console.log('🎯 Accessories Drawer: Initializing...');
    
    const trigger = document.querySelector('[data-accessories-trigger]');
    const drawer = document.querySelector('[data-accessories-drawer]');
    const overlay = document.querySelector('[data-accessories-overlay]');
    const closeBtn = document.querySelector('[data-accessories-close]');

    // Variant-specific metafield accessories data (rendered by Liquid)
    const sectionId = drawer?.dataset?.sectionId;
    let variantAccessoriesMap = null;
    if (sectionId) {
      const dataEl = document.getElementById(`Accessories-Metafield-Variant-Data-${sectionId}`);
      if (dataEl && dataEl.textContent) {
        try {
          variantAccessoriesMap = JSON.parse(dataEl.textContent);
          console.log('🧩 Loaded variant accessories map for section', sectionId);
        } catch (e) {
          console.warn('⚠️ Failed to parse variant accessories map JSON', e);
        }
      }
    }
    
    console.log('🎯 Elements found:', {
      trigger: !!trigger,
      drawer: !!drawer,
      overlay: !!overlay,
      closeBtn: !!closeBtn,
      triggerHtml: trigger ? trigger.outerHTML.substring(0, 100) : 'null'
    });
    
    // DEBUG: Log overlay element details
    if (overlay) {
      console.log('🔍 Overlay Details:', {
        classList: overlay.classList.toString(),
        computedStyle: window.getComputedStyle(overlay).backdropFilter,
        position: window.getComputedStyle(overlay).position,
        zIndex: window.getComputedStyle(overlay).zIndex,
        opacity: window.getComputedStyle(overlay).opacity,
        pointerEvents: window.getComputedStyle(overlay).pointerEvents
      });
    }
    
    // DEBUG: Log drawer element details
    if (drawer) {
      console.log('🔍 Drawer Details:', {
        classList: drawer.classList.toString(),
        position: window.getComputedStyle(drawer).position,
        right: window.getComputedStyle(drawer).right,
        zIndex: window.getComputedStyle(drawer).zIndex
      });
    }
    
    if (!trigger) {
      /* No dedicated trigger button is fine — the OEM PDP opens this drawer via
         window.AccessoriesDrawer.open() (oem-accessories-trigger card). */
      console.warn('ℹ️ Accessories trigger button not found — drawer still available via window.AccessoriesDrawer.');
    }
    
    if (!drawer || !overlay) {
      console.error('❌ Drawer or overlay not found!');
      return;
    }
    
    console.log('✅ All elements found! Setting up event listeners...');
    
    // Track current snap point (moved to outer scope for open/close access)
    let currentSnapPoint = 0;
    
    // Track scroll position for body lock (iOS Safari fix)
    let savedScrollPosition = 0;

    // Lock body scroll (iOS Safari compatible)
    const lockBodyScroll = () => {
      savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.top = `-${savedScrollPosition}px`;
      document.body.classList.add('drawer-open');
    };

    // Unlock body scroll and restore position
    const unlockBodyScroll = () => {
      document.body.classList.remove('drawer-open');
      document.body.style.top = '';
      window.scrollTo(0, savedScrollPosition);
    };

    // Open drawer (named so the OEM PDP card can call it via window.AccessoriesDrawer)
    const open = () => {
      // IMPORTANT: Sync content BEFORE starting animation to prevent Android jitter
      syncDrawerToCurrentVariant();
      // Reflect anything already in the cart (added here or on a previous visit).
      syncAddedState();

      // Reset snap point to full open
      currentSnapPoint = 0;
      drawer.classList.remove('is-half');
      drawer.classList.add('is-full');
      drawer.style.removeProperty('transform');

      // Small delay to let DOM settle before animation (prevents Android jitter)
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        overlay.classList.add('is-open');
        // iOS Safari: Lock body scroll with position fixed + scroll position save
        lockBodyScroll();
        console.log('✅ Drawer opened at full height');
      });
    };

    if (trigger) {
      trigger.addEventListener('click', (e) => {
        console.log('🚀 Trigger clicked!');
        e.preventDefault();
        e.stopPropagation();
        open();
      }, true);
    }

    // Close drawer
    const close = () => {
      console.log('🔒 Closing drawer...');
      drawer.classList.remove('is-open', 'is-half', 'is-full', 'is-dragging');
      overlay.classList.remove('is-open');
      // Reset snap point for next open
      currentSnapPoint = 0;
      // Clear all inline styles that might have been set during drag/snap
      drawer.style.removeProperty('transform');
      drawer.style.transition = '';
      overlay.style.opacity = '';
      overlay.style.transition = '';
      // iOS Safari: Unlock body scroll and restore position
      unlockBodyScroll();
      console.log('✅ Drawer closed');
    };
    
    if (closeBtn) {
      closeBtn.addEventListener('click', close);
      console.log('✅ Close button listener added');
    } else {
      console.warn('⚠️ Close button not found');
    }

    /* Public API — the OEM PDP "Recommended Accessories" card calls this. */
    window.AccessoriesDrawer = { open: open, close: close };

    // Click overlay to close
    if (overlay) {
      overlay.addEventListener('click', close);
    }

    // ========================================
    // SNAP POINT DRAWER (ChatGPT-style)
    // Supports: Full (0%), Half (50%), Closed (100%)
    // Content remains scrollable at any snap point
    // ========================================
    let swipeStartY = 0;
    let swipeCurrentY = 0;
    let swipeStartTime = 0;
    let isSwiping = false;
    // currentSnapPoint is declared above in the open function scope
    
    // Snap points as percentage of drawer height from top
    // 0 = fully open, 0.5 = half height, 1 = closed
    const SNAP_POINTS = [0, 0.5, 1];
    const VELOCITY_THRESHOLD = 0.5; // pixels per millisecond for fast flick

    // Get drawer content for scroll containment
    const drawerContent = drawer.querySelector('.accessories-drawer__content');
    
    // Get the translateY value for a snap point
    const getSnapTranslateY = (snapPoint, drawerHeight) => {
      // snapPoint 0 = translateY(0), 0.5 = translateY(50%), 1 = translateY(100%)
      return snapPoint * drawerHeight;
    };
    
    // Find nearest snap point based on current position and velocity
    const findNearestSnapPoint = (currentPercent, velocity) => {
      // If fast swipe down, go to next snap point
      if (velocity > VELOCITY_THRESHOLD) {
        const nextPoints = SNAP_POINTS.filter(p => p > currentSnapPoint);
        return nextPoints.length > 0 ? nextPoints[0] : 1;
      }
      // If fast swipe up, go to previous snap point
      if (velocity < -VELOCITY_THRESHOLD) {
        const prevPoints = SNAP_POINTS.filter(p => p < currentSnapPoint).reverse();
        return prevPoints.length > 0 ? prevPoints[0] : 0;
      }
      // Otherwise snap to nearest
      let nearest = SNAP_POINTS[0];
      let minDist = Math.abs(currentPercent - SNAP_POINTS[0]);
      for (const point of SNAP_POINTS) {
        const dist = Math.abs(currentPercent - point);
        if (dist < minDist) {
          minDist = dist;
          nearest = point;
        }
      }
      return nearest;
    };
    
    // Animate to a snap point
    const animateToSnapPoint = (snapPoint, drawerHeight) => {
      drawer.classList.remove('is-dragging');
      drawer.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      overlay.style.transition = 'opacity 0.3s ease';
      
      const translateY = getSnapTranslateY(snapPoint, drawerHeight);
      drawer.style.setProperty('transform', `translateY(${translateY}px)`, 'important');
      
      // Adjust overlay opacity based on snap point
      const overlayOpacity = 1 - (snapPoint * 0.6);
      overlay.style.opacity = String(Math.max(0, overlayOpacity));
      
      currentSnapPoint = snapPoint;
      
      // Update drawer state class
      drawer.classList.remove('is-half', 'is-full');
      if (snapPoint === 0) {
        drawer.classList.add('is-full');
      } else if (snapPoint === 0.5) {
        drawer.classList.add('is-half');
      }
      
      setTimeout(() => {
        drawer.style.transition = '';
        // If closed, actually close the drawer
        if (snapPoint === 1) {
          drawer.style.removeProperty('transform');
          overlay.style.transition = '';
          overlay.style.opacity = '';
          close();
          currentSnapPoint = 0; // Reset for next open
        }
      }, 300);
    };

    drawer.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      const drawerRect = drawer.getBoundingClientRect();
      const touchOffsetY = touch.clientY - drawerRect.top;

      // Allow swipe from top 100px of drawer (header/handle area)
      if (touchOffsetY <= 100) {
        swipeStartY = touch.clientY;
        swipeCurrentY = touch.clientY;
        swipeStartTime = Date.now();
        isSwiping = true;
        // Add dragging class to override CSS transform
        drawer.classList.add('is-dragging');
        console.log('🖐️ Swipe started at Y:', touchOffsetY, 'currentSnapPoint:', currentSnapPoint);
      }
    }, { passive: true });

    drawer.addEventListener('touchmove', (e) => {
      if (!isSwiping) return;

      const touch = e.touches[0];
      swipeCurrentY = touch.clientY;
      const deltaY = swipeCurrentY - swipeStartY;
      const drawerHeight = drawer.offsetHeight;
      
      // Calculate current position including the snap point offset
      const currentOffset = getSnapTranslateY(currentSnapPoint, drawerHeight);
      const newTranslateY = currentOffset + deltaY;
      
      // Clamp to valid range (0 to drawerHeight)
      const clampedTranslateY = Math.max(0, Math.min(newTranslateY, drawerHeight));
      
      // iOS FIX: Use setProperty with 'important' priority
      drawer.style.setProperty('transform', `translateY(${clampedTranslateY}px)`, 'important');
      
      // Fade overlay based on position
      const progress = clampedTranslateY / drawerHeight;
      overlay.style.opacity = String(Math.max(0, 1 - (progress * 0.6)));
    }, { passive: true });

    drawer.addEventListener('touchend', () => {
      if (!isSwiping) return;

      const deltaY = swipeCurrentY - swipeStartY;
      const elapsed = Date.now() - swipeStartTime;
      const velocity = elapsed > 0 ? deltaY / elapsed : 0;
      const drawerHeight = drawer.offsetHeight;
      
      // Calculate where we ended up as a percentage
      const currentOffset = getSnapTranslateY(currentSnapPoint, drawerHeight);
      const finalTranslateY = Math.max(0, Math.min(currentOffset + deltaY, drawerHeight));
      const finalPercent = finalTranslateY / drawerHeight;

      console.log('🖐️ Swipe ended:', { 
        deltaY, 
        velocity: velocity.toFixed(2), 
        finalPercent: (finalPercent * 100).toFixed(0) + '%',
        currentSnapPoint 
      });

      // Find the snap point to animate to
      const targetSnapPoint = findNearestSnapPoint(finalPercent, velocity);
      console.log('📍 Snapping to:', targetSnapPoint === 0 ? 'full' : targetSnapPoint === 0.5 ? 'half' : 'closed');
      
      animateToSnapPoint(targetSnapPoint, drawerHeight);

      isSwiping = false;
      swipeStartY = 0;
      swipeCurrentY = 0;
    }, { passive: true });

    drawer.addEventListener('touchcancel', () => {
      drawer.classList.remove('is-dragging');
      drawer.style.transition = '';
      // Snap back to current snap point
      const drawerHeight = drawer.offsetHeight;
      animateToSnapPoint(currentSnapPoint, drawerHeight);
      isSwiping = false;
    }, { passive: true });

    // ========================================
    // SCROLL CONTAINMENT - prevent background scroll on Android/iOS
    // Content area should scroll freely, only block at boundaries
    // ========================================
    let contentTouchStartY = 0;
    let contentScrollStartTop = 0;

    if (drawerContent) {
      drawerContent.addEventListener('touchstart', (e) => {
        contentTouchStartY = e.touches[0].clientY;
        contentScrollStartTop = drawerContent.scrollTop;
      }, { passive: true });

      drawerContent.addEventListener('touchmove', (e) => {
        // Allow scrolling within content - only prevent overscroll
        const { scrollTop, scrollHeight, clientHeight } = drawerContent;
        const touchY = e.touches[0].clientY;
        const deltaY = contentTouchStartY - touchY; // positive = scrolling down

        const atTop = scrollTop <= 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
        const hasScrollableContent = scrollHeight > clientHeight;

        // Only prevent default at boundaries to stop body scroll
        // But allow the content to scroll if there's scrollable content
        if (!hasScrollableContent) {
          // No scrollable content, prevent to avoid body scroll
          e.preventDefault();
        } else if ((atTop && deltaY < 0) || (atBottom && deltaY > 0)) {
          // At boundary trying to scroll past it
          e.preventDefault();
        }
        // Otherwise, let the scroll happen naturally
      }, { passive: false });
    }

    // Prevent overlay touch from scrolling body
    overlay.addEventListener('touchmove', (e) => {
      e.preventDefault();
    }, { passive: false });

    // Prevent touch on drawer header from scrolling body (except when swiping to close)
    const drawerHeader = drawer.querySelector('.accessories-drawer__header');
    if (drawerHeader) {
      drawerHeader.addEventListener('touchmove', (e) => {
        if (!isSwiping) {
          e.preventDefault();
        }
      }, { passive: false });
    }

    // Cancel/Add button - define early so scrollToAddButton can use it
    const cancelBtn = document.querySelector('[data-accessories-cancel]');

    // ========================================
    // SCROLL TO ADD BUTTON when item selected
    // Minimal scroll - just enough to show button with 5px margin
    // ========================================
    function scrollToAddButton() {
      if (!cancelBtn || !drawerContent) {
        console.log('❌ scrollToAddButton: missing elements', { cancelBtn: !!cancelBtn, drawerContent: !!drawerContent });
        return;
      }

      // Small delay to let UI update
      setTimeout(() => {
        // Get button position relative to drawer content viewport
        const btnRect = cancelBtn.getBoundingClientRect();
        const contentRect = drawerContent.getBoundingClientRect();

        // Check if button bottom is below the visible content area
        if (btnRect.bottom > contentRect.bottom) {
          // Scroll just enough to show the button with 5px margin
          const currentScroll = drawerContent.scrollTop;
          const extraScroll = btnRect.bottom - contentRect.bottom + 5;

          drawerContent.scrollTo({
            top: currentScroll + extraScroll,
            behavior: 'smooth'
          });
          console.log('📍 Scrolled to Add button', { extraScroll });
        } else {
          // Button is already visible
          console.log('📍 Add button already visible');
        }
      }, 150);
    }
    const selectedAccessories = new Set();
    let lastSyncedVariantId = null;

    function restoreSelectionsToUI() {
      drawer.querySelectorAll('.accessory-item__checkbox').forEach(cb => {
        const checked = selectedAccessories.has(String(cb.value));
        cb.checked = checked;
        const item = cb.closest('.accessory-item');
        if (item) item.classList.toggle('is-selected', checked);
      });
      updateCancelButton();
    }
    
    // Update cancel button state and text
    function updateCancelButton() {
      if (!cancelBtn) return;
      
      if (selectedAccessories.size > 0) {
        const count = selectedAccessories.size;
        
        // Calculate total price
        let totalPrice = 0;
        
        selectedAccessories.forEach(variantId => {
          const item = drawer.querySelector(`[data-variant-id="${variantId}"]`);
          if (item) {
            // Price is stored in cents
            const priceInCents = parseInt(item.dataset.price || item.dataset.priceCents || '0', 10);
            totalPrice += priceInCents;
            console.log('💰 Price read:', { variantId, priceInCents, runningTotal: totalPrice });
          }
        });
        
        // Format price (convert cents to dollars)
        const formattedPrice = (totalPrice / 100).toFixed(2);
        // Always use $ symbol
        const currencySymbol = '$';
        const countLabel = count === 1 ? 'Item' : 'Items';
        
        cancelBtn.classList.add('has-selection');
        cancelBtn.innerHTML = `Add ${count} ${countLabel} - ${currencySymbol}${formattedPrice}`;
        cancelBtn.dataset.accessoriesTotal = formattedPrice;
        cancelBtn.dataset.accessoriesCount = count;
        
        console.log('✅ Button updated:', { count, totalPrice, formattedPrice });
      } else {
        cancelBtn.classList.remove('has-selection');
        cancelBtn.innerHTML = 'Cancel';
        cancelBtn.dataset.accessoriesTotal = '';
        cancelBtn.dataset.accessoriesCount = '0';
      }
    }
    
    /* Added-state (owner 2026-07-30): mirror what is ACTUALLY in the cart onto
       the drawer rows and the trigger card, so the confirmation outlives the
       toast and survives a page reload. */
    async function syncAddedState() {
      let cart;
      try {
        cart = await fetch('/cart.js?_fd=0', { credentials: 'same-origin' }).then(r => r.json());
      } catch (e) { return; }

      const qtyByVariant = new Map();
      (cart.items || []).forEach(it => qtyByVariant.set(String(it.variant_id), it.quantity));

      let inCartCount = 0;
      drawer.querySelectorAll('.accessory-item').forEach(item => {
        const cb = item.querySelector('.accessory-item__checkbox');
        if (!cb) return;
        const inCart = (qtyByVariant.get(String(cb.value)) || 0) > 0;
        item.classList.toggle('is-in-cart', inCart);
        if (inCart) inCartCount++;
      });

      document.querySelectorAll('[data-accessories-trigger]').forEach(card => {
        card.classList.toggle('has-added', inCartCount > 0);
        const host = card.querySelector('.product-upsell-card__text') || card;
        let badge = card.querySelector('[data-acc-added]');
        if (inCartCount > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'product-upsell-card__added';
            badge.setAttribute('data-acc-added', '');
            host.appendChild(badge);
          }
          badge.textContent = '✓ ' + inCartCount + (inCartCount === 1 ? ' item in cart' : ' items in cart');
        } else if (badge) {
          badge.remove();
        }
      });
      console.log('🧾 Accessories in cart:', inCartCount);
    }
    syncAddedState();

    if (cancelBtn) {
      cancelBtn.addEventListener('click', async (e) => {
          e.preventDefault();

        if (selectedAccessories.size > 0) {
          const variantIds = Array.from(selectedAccessories);

          /* Owner 2026-07-30: ALWAYS add straight to the cart. This used to be
             gated on the paint already being in the cart; otherwise the picks
             were bundled onto the main ATC button, whose qty/price badge is now
             hidden — so the shopper saw nothing happen, and lost the items
             entirely if they never pressed Add to Cart. Mirrors the one-tap add
             on mcc-alt-oem (assets/oem-pdp-blocks.js). */
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: variantIds.map(v => ({ id: parseInt(v, 10), quantity: 1 })) })
            });
            const cart = await fetch('/cart.js?_fd=0', { credentials: 'same-origin' }).then(r => r.json());
            document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
            if (window.publish && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'accessories-drawer-direct', cartData: cart });
            }
            const cd = document.querySelector('cart-drawer');
            if (cd && typeof cd._refreshSections === 'function') cd._refreshSections();
            if (window.DYCToast) {
              DYCToast.show({
                type: 'cart',
                title: 'Added to cart',
                desc: variantIds.length + (variantIds.length === 1 ? ' accessory' : ' accessories') + ' added',
                actionText: 'View Cart',
                action: function () {
                  const d = document.querySelector('cart-drawer');
                  if (d && typeof d.show === 'function') d.show();
                }
              });
            }
            console.log('🛒 Accessories added directly to cart:', variantIds);
          } catch (err) {
            console.warn('⚠️ Direct accessories add failed', err);
          }

          selectedAccessories.clear();
          drawer.querySelectorAll('.accessory-item__checkbox').forEach(cb => {
            cb.checked = false;
            const it = cb.closest('.accessory-item');
            it && it.classList.remove('is-selected');
          });
          updateCancelButton();
          /* Nothing is armed on the main ATC any more — clear any stale bundle
             state in product-upsell-widget so the main form can't re-add these. */
          document.dispatchEvent(new CustomEvent('dyc:accessories-clear', { detail: { sectionId } }));
          await syncAddedState();
          close();
          return;
        }

        // Cancel: clear local selections & notify listeners
            selectedAccessories.clear();
            drawer.querySelectorAll('.accessory-item__checkbox').forEach(cb => {
              cb.checked = false;
          const item = cb.closest('.accessory-item');
          item && item.classList.remove('is-selected');
            });
        updateCancelButton();
            
        document.dispatchEvent(new CustomEvent('dyc:accessories-clear', { detail: { sectionId } }));
        console.log('📣 Dispatched dyc:accessories-clear');

            close();
      });
      console.log('✅ Cancel/Add button listener added');
    }
    
    if (overlay) {
      overlay.addEventListener('click', close);
      console.log('✅ Overlay listener added');
    }
    
    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });
    console.log('✅ ESC key listener added');

    // Checkbox toggle for accessories - using delegation for reliability
    const handleCheckboxChange = (inputEl) => {
      if (!inputEl) return;
      
      const item = inputEl.closest('.accessory-item');
      const variantId = inputEl.value;
      const priceCents = parseInt(inputEl.dataset.priceCents || '0', 10);
      
      console.log('🔄 Checkbox changed:', { variantId, checked: inputEl.checked, priceCents });
      
      if (inputEl.checked) {
        selectedAccessories.add(variantId);
        item && item.classList.add('is-selected');
      } else {
        selectedAccessories.delete(variantId);
        item && item.classList.remove('is-selected');
      }
      
      updateCancelButton();
      console.log(`📦 Selection updated:`, Array.from(selectedAccessories));

      // Scroll to Add button if item was selected (so user sees the action button)
      if (inputEl.checked) {
        scrollToAddButton();
      }

      /* Owner 2026-07-30: no longer arms the main ATC button while selecting.
         The drawer adds straight to the cart on "Add N Items", so priming the
         main form here would let a shopper who ticks a box and then closes the
         drawer (X / overlay) silently add accessories on the next Add to Cart. */
    };

    // If accessories were bundled successfully, clear drawer UI to avoid stale checked state
    document.addEventListener('dyc:accessories-clear', () => {
      selectedAccessories.clear();
      drawer.querySelectorAll('.accessory-item__checkbox').forEach(cb => {
        cb.checked = false;
        const item = cb.closest('.accessory-item');
        item && item.classList.remove('is-selected');
      });
      updateCancelButton();
    });
    
    // Use ONLY delegated event listener (more reliable, no duplication)
    drawer.addEventListener('change', (e) => {
      if (e.target && e.target.classList.contains('accessory-item__checkbox')) {
        handleCheckboxChange(e.target);
      }
    });
      
      // Make entire item clickable
    drawer.addEventListener('click', (e) => {
      const item = e.target.closest('.accessory-item');
      if (!item) return;
      
      // Don't trigger if clicking the label/checkbox directly (they handle it themselves)
      if (e.target.closest('.accessory-item__checkbox-label') || 
          e.target.closest('.accessory-item__checkbox') ||
          e.target.classList.contains('accessory-item__checkbox')) {
            return;
          }
      
          // Toggle checkbox
      const checkbox = item.querySelector('.accessory-item__checkbox');
      if (checkbox) {
          checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    
    console.log(`🛒 Accessories: Event delegation setup complete`);

    console.log('✅ Accessories Drawer: Fully initialized and ready');

    // =========================================================================
    // SELF-CONTAINED CART ADDITION
    // This allows the accessories drawer to work independently without
    // requiring the upsell widget. When upsell widget IS present, it will
    // handle the cart addition. When it's NOT present, we handle it ourselves.
    // =========================================================================

    /**
     * Check if the upsell widget is present and will handle accessories.
     * Returns true if upsell widget exists and has accessories support.
     */
    function upsellWidgetWillHandleAccessories() {
      const upsellWidget = document.querySelector('product-upsell-widget');
      // Check if the widget exists AND has the selectedAccessories property
      // (the live-theme version doesn't have this property)
      if (upsellWidget && typeof upsellWidget.selectedAccessories !== 'undefined') {
        console.log('🔗 Upsell widget detected with accessories support - it will handle cart addition');
        return true;
      }
      console.log('🔗 No upsell widget with accessories support - accessories drawer will handle cart addition');
      return false;
    }

    /**
     * Find the main product form on the page.
     */
    function findProductForm() {
      // Try section-specific forms first
      if (sectionId) {
        const sectionForm = document.querySelector(`product-form[data-section="${sectionId}"] form`);
        if (sectionForm) return sectionForm;

        const mccForm = document.querySelector(`mcc-product-form[data-section="${sectionId}"] form`);
        if (mccForm) return mccForm;
      }

      // Fallback to any product form
      const anyForm = document.querySelector('product-form form, mcc-product-form form');
      if (anyForm) return anyForm;

      // Last resort: any cart add form
      return document.querySelector('form[action*="/cart/add"]');
    }

    /**
     * Add items to cart via Shopify AJAX API.
     */
    async function addItemsToCart(items) {
      const results = [];

      for (const item of items) {
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              id: item.id,
              quantity: item.quantity || 1
            })
          });

          const result = await response.json();

          if (result.status || result.description) {
            console.error('❌ Error adding item:', item.id, result.description || result.message);
            results.push({ id: item.id, success: false, error: result.description || result.message });
          } else {
            console.log('✅ Item added:', item.id);
            results.push({ id: item.id, success: true });
          }

          // Small delay between items for discount calculation
          if (items.indexOf(item) < items.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (err) {
          console.error('❌ Network error adding item:', item.id, err);
          results.push({ id: item.id, success: false, error: err.message });
        }
      }

      return results;
    }

    /**
     * Open the cart drawer after adding items.
     */
    async function openCartDrawer() {
      // Method 1: Use Shopify's cart drawer if available
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        // Fetch fresh cart HTML
        try {
          const cartSection = cartDrawer.dataset.section;
          if (cartSection) {
            const timestamp = Date.now();
            const response = await fetch(`/cart?section_id=${cartSection}&t=${timestamp}`);
            const html = await response.text();

            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const newDetails = doc.querySelector('#Cart-Drawer-Details');
            const existingDetails = document.querySelector('#Cart-Drawer-Details');

            if (newDetails && existingDetails) {
              existingDetails.innerHTML = newDetails.innerHTML;
            }
          }

          // Open the drawer
          cartDrawer.setAttribute('open', '');
          cartDrawer.classList.add('is-open', 'active');
          document.body.classList.add('overflow-hidden');

          console.log('🛒 Cart drawer opened');
        } catch (err) {
          console.warn('⚠️ Error refreshing cart drawer:', err);
          // Still try to open it
          cartDrawer.setAttribute('open', '');
        }
        return;
      }

      // Method 2: Trigger cart icon click
      const cartIcon = document.querySelector('[data-cart-toggle], .cart-icon-bubble, a[href="/cart"]');
      if (cartIcon) {
        cartIcon.click();
        return;
      }

      // Method 3: Redirect to cart page
      console.log('🛒 No cart drawer found, redirecting to cart page');
      window.location.href = '/cart';
    }

    /**
     * Update cart icon bubble count.
     */
    async function updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
          const visible = bubble.querySelector('span[aria-hidden="true"]');
          if (visible) visible.textContent = cart.item_count;

          const sr = bubble.querySelector('.visually-hidden');
          if (sr) sr.textContent = cart.item_count === 1 ? '1 item' : `${cart.item_count} items`;
        });

        // Also update any other cart count elements
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
        });

        // Publish cart update event for other listeners
        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'accessories-drawer', cart });
        }
      } catch (err) {
        console.warn('⚠️ Error updating cart count:', err);
      }
    }

    /**
     * Handle form submission with accessories.
     * If upsell widget is present, let it handle everything.
     * If not, we add accessories ourselves then submit the main product.
     */
    async function handleFormSubmitWithAccessories(form) {
      // If no accessories selected, let normal submission happen
      if (selectedAccessories.size === 0) {
        console.log('🛒 No accessories selected, allowing normal form submission');
        return false; // Don't prevent default
      }

      // Check if upsell widget will handle this
      if (upsellWidgetWillHandleAccessories()) {
        console.log('🛒 Upsell widget will handle accessories, allowing normal submission');
        return false; // Don't prevent default - upsell widget handles it
      }

      // We need to handle this ourselves
      console.log('🛒 Accessories drawer handling cart addition independently');

      // Get main product info from the form
      const formData = new FormData(form);
      const mainVariantId = formData.get('id');
      const mainQuantity = parseInt(formData.get('quantity') || '1', 10);

      if (!mainVariantId) {
        console.error('❌ No main variant ID found in form');
        return false;
      }

      // Build items array: main product + accessories
      const items = [
        { id: mainVariantId, quantity: mainQuantity }
      ];

      // Add accessories
      selectedAccessories.forEach(variantId => {
        items.push({ id: variantId, quantity: 1 });
      });

      console.log('🛒 Adding items to cart:', items);

      // Show loading state on submit button
      const submitBtn = form.querySelector('[type="submit"], .add-cart-button, button[name="add"]');
      const originalBtnContent = submitBtn ? submitBtn.innerHTML : null;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span style="display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%;"><span class="loading-spinner"></span> Adding Accessories...</span>';
      }

      try {
        // Add all items to cart
        const results = await addItemsToCart(items);

        const successCount = results.filter(r => r.success).length;
        console.log(`✅ Added ${successCount}/${items.length} items to cart`);

        // Clear accessories selections
        selectedAccessories.clear();
        drawer.querySelectorAll('.accessory-item__checkbox').forEach(cb => {
          cb.checked = false;
          const item = cb.closest('.accessory-item');
          item && item.classList.remove('is-selected');
        });
        updateCancelButton();

        // Notify listeners that accessories were cleared
        document.dispatchEvent(new CustomEvent('dyc:accessories-clear', { detail: { sectionId } }));

        // Update cart count
        await updateCartCount();

        // Open cart drawer
        await openCartDrawer();

        return true; // We handled it, prevent default

      } catch (err) {
        console.error('❌ Error adding to cart:', err);
        alert('There was an error adding items to your cart. Please try again.');
        return true; // We tried to handle it, prevent default to avoid double-submission

      } finally {
        // Restore button state
        if (submitBtn && originalBtnContent) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalBtnContent;
        }
      }
    }

    /**
     * Attach form submit listener for self-contained cart addition.
     */
    function initFormSubmitListener() {
      const form = findProductForm();

      if (!form) {
        console.warn('🛒 Accessories drawer: No product form found for submit interception');
        return;
      }

      // Don't attach if already attached
      if (form.dataset.accessoriesListenerAttached) {
        console.log('🛒 Accessories drawer: Form listener already attached');
        return;
      }

      form.dataset.accessoriesListenerAttached = 'true';

      form.addEventListener('submit', async (e) => {
        console.log('🛒 Form submit intercepted by accessories drawer');

        const shouldPrevent = await handleFormSubmitWithAccessories(form);

        if (shouldPrevent) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true); // Use capture phase to intercept early

      console.log('✅ Accessories drawer: Form submit listener attached');
    }

    // Initialize form listener after a short delay to ensure DOM is ready
    setTimeout(() => {
      initFormSubmitListener();
    }, 150);

    // Also re-attach when variant changes (form might be recreated)
    document.addEventListener('variant:change', () => {
      setTimeout(() => {
        const form = findProductForm();
        if (form && !form.dataset.accessoriesListenerAttached) {
          initFormSubmitListener();
        }
      }, 100);
    });

    // =========================================================================
    // END SELF-CONTAINED CART ADDITION
    // =========================================================================

    /**
     * Get currently selected main product variant id from the page.
     */
    function getCurrentVariantId() {
      const input = document.querySelector('input[name="id"]');
      const v = input ? parseInt(input.value, 10) : NaN;
      return Number.isFinite(v) ? v : null;
    }

    /**
     * Render metafield accessories for a given variant.
     * This swaps ONLY the metafield portion of the drawer, leaving manual items intact.
     */
    function renderMetafieldAccessories(variantId) {
      if (!drawer) return;
      const container = drawer.querySelector('[data-accessories-metafield-items]');
      if (!container) return;

      if (!variantAccessoriesMap || !variantId) {
        container.innerHTML = '';
        return;
      }

      const items = variantAccessoriesMap[String(variantId)] || [];
      const html = (items || [])
        .map((item, idx) => {
          if (!item || !item.product_id || !item.variant_id) return '';

          const isAvailable = !!item.available;
          const hasDiscount = !!item.has_discount;
          const badgeHtml = hasDiscount && item.save_html
            ? `<div class="accessory-item__badge">SAVE ${item.save_html}</div>`
            : '';

          const imageHtml = item.image
            ? `<img src="${item.image}" alt="${(item.title || '').replace(/"/g, '&quot;')}" class="accessory-item__image">`
            : `<div class="accessory-item__image accessory-item__image--placeholder">
                 <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                   <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                   <circle cx="8.5" cy="8.5" r="1.5"></circle>
                   <polyline points="21 15 16 10 5 21"></polyline>
                 </svg>
               </div>`;

          const pricesHtml = hasDiscount
            ? `<span class="accessory-item__price--compare">${item.compare_at_html || ''}</span>
               <span class="accessory-item__price accessory-item__price--sale">${item.price_html || ''}</span>`
            : `<span class="accessory-item__price">${item.price_html || ''}</span>`;

          const soldOutHtml = !isAvailable ? `<div class="accessory-item__sold-out">SOLD OUT</div>` : '';

          const checkboxId = `accessory-meta-${variantId}-${item.product_id}-${idx}`;
          const checkboxHtml = isAvailable
            ? `<input
                 type="checkbox"
                 class="accessory-item__checkbox"
                 id="${checkboxId}"
                 value="${item.variant_id}"
                 data-price="${item.price_cents || 0}"
                 data-price-cents="${item.price_cents || 0}"
               >
               <label for="${checkboxId}" class="accessory-item__checkbox-label">
                 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                   <polyline points="20 6 9 17 4 12"></polyline>
                 </svg>
               </label>`
            : `<button type="button" class="accessory-item__add" disabled aria-label="Unavailable"></button>`;

          const descHtml = item.blurb ? `<div class="accessory-item__description">${item.blurb}</div>` : '';

          return `
            <div class="accessory-item"
                 data-source="metafield"
                 data-product-id="${item.product_id}"
                 data-variant-id="${item.variant_id}"
                 data-price="${item.price_cents || 0}"
                 data-price-cents="${item.price_cents || 0}">
              <div class="accessory-item__image-container">
                ${badgeHtml}
                ${imageHtml}
              </div>
              <div class="accessory-item__info">
                <div class="accessory-item__prices">${pricesHtml}</div>
                <div class="accessory-item__name">${item.title || ''}${item.variant_title && item.variant_title !== 'Default Title' ? ` <span class="accessory-item__variant-name">– ${item.variant_title}</span>` : ''}</div>
                ${descHtml}
                ${soldOutHtml}
              </div>
              <div class="accessory-item__checkbox-wrapper">
                ${checkboxHtml}
              </div>
            </div>
          `;
        })
        .join('');

      container.innerHTML = html;

      // Re-apply selection state to freshly rendered metafield items.
      restoreSelectionsToUI();
      // Freshly rendered rows lose their in-cart marks — re-derive from the cart.
      syncAddedState();
    }

    /**
     * Clear any current selections and notify listeners.
     */
    function clearSelections() {
      selectedAccessories.clear();
      drawer.querySelectorAll('.accessory-item__checkbox').forEach(cb => {
        cb.checked = false;
        const item = cb.closest('.accessory-item');
        item && item.classList.remove('is-selected');
      });
      updateCancelButton();
      document.dispatchEvent(new CustomEvent('dyc:accessories-clear', { detail: { sectionId } }));
    }

    /**
     * Sync drawer metafield items to current selected variant.
     * IMPORTANT: We preserve user selections across variant changes.
     * Only metafield accessories that are no longer available get removed.
     */
    function syncDrawerToCurrentVariant(variantIdOverride) {
      const variantId = variantIdOverride || getCurrentVariantId();
      if (!variantId) return;

      if (variantAccessoriesMap) {
        const didVariantChange = lastSyncedVariantId !== null && String(lastSyncedVariantId) !== String(variantId);
        lastSyncedVariantId = String(variantId);

        // Re-render metafield items for this variant.
        renderMetafieldAccessories(variantId);

        if (didVariantChange) {
          // DON'T clear all selections - preserve user's choices!
          // Only remove selections for metafield items that are no longer available
          const newMetafieldItems = variantAccessoriesMap[String(variantId)] || [];
          const newMetafieldVariantIds = new Set(newMetafieldItems.map(item => String(item.variant_id)));

          // Check each selected accessory - only remove if it was a metafield item that's no longer available
          const selectionsToRemove = [];
          selectedAccessories.forEach(variantId => {
            // Check if this was a metafield item (not manual)
            const itemEl = drawer.querySelector(`[data-variant-id="${variantId}"][data-source="metafield"]`);
            if (itemEl && !newMetafieldVariantIds.has(variantId)) {
              // This was a metafield item that's no longer available for new variant
              selectionsToRemove.push(variantId);
            }
          });

          // Remove only the unavailable metafield selections
          selectionsToRemove.forEach(variantId => {
            selectedAccessories.delete(variantId);
            console.log('🔄 Removed unavailable metafield accessory:', variantId);
          });

          // Update the drawer's own button if we removed anything (the main ATC
          // is no longer armed from here — see the checkbox handler note).
          if (selectionsToRemove.length > 0) {
            updateCancelButton();
          }
        }

        // Always restore selections to UI (for both same variant and changed variant)
        restoreSelectionsToUI();
        console.log('🔄 Accessories Drawer: synced to variant', variantId, '- preserved', selectedAccessories.size, 'selections');
      }
    }

    // Listen for variant changes from theme pub/sub
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        try {
          const v = event?.data?.variant;
          if (v && v.id) syncDrawerToCurrentVariant(v.id);
        } catch (_) {}
      });
    }

    // Listen for custom DOM variant event (used by custom selectors)
    document.addEventListener('variant:change', (e) => {
      try {
        const v = e?.detail?.variant;
        if (v && v.id) syncDrawerToCurrentVariant(v.id);
      } catch (_) {}
    });
    
    // CSS handles all styling - no inline styles needed
    // This prevents MutationObserver loops and Android performance issues
    console.log('✅ Accessories Drawer: Ready (CSS-only mode)');
  }
})();

